import React, { useEffect, useState } from "react";
import { api, brl, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Banknote, Filter, CheckCircle2, XCircle, Search, DollarSign, X, ShieldAlert } from "lucide-react";

const PURPLE = "#2B1E4A";

const WD_STATUS = {
  solicitado: ["Solicitado", "bg-amber-100 text-amber-700"],
  em_analise: ["Em analise", "bg-blue-100 text-blue-700"],
  aprovado: ["Aprovado", "bg-indigo-100 text-indigo-700"],
  pago: ["Pago", "bg-emerald-100 text-emerald-700"],
  rejeitado: ["Rejeitado", "bg-red-100 text-red-700"],
  cancelado: ["Cancelado", "bg-gray-100 text-gray-500"],
};

export default function AdminEletrosSaques() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ status: "", date_from: "", date_to: "", min_amount: "" });
  const [rejectModal, setRejectModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      params.scope = "admin";
      setRows((await api.get("/eletros/withdrawals", { params })).data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const act = async (w, action, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/eletros/withdrawals/${w.id}/status`, { action, ...body });
      toast.success("Atualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const pending = rows.filter((r) => ["solicitado", "em_analise", "aprovado"].includes(r.status));
  const totalPending = pending.reduce((a, r) => a + Number(r.amount), 0);
  const totalPaid = rows.filter((r) => r.status === "pago").reduce((a, r) => a + Number(r.amount), 0);

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="admin-eletros-saques-page">
      <div className="flex items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "#F5B400" }}><Banknote size={24} className="text-[#2B1E4A]" /></div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#F5B400" }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Saques Eletros</h1>
          <p className="text-[12px] text-white/70">Aprovacao e repasse manual (sem PIX automatico de saida)</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Mini label="Solicitacoes em aberto" value={String(pending.length)} />
        <Mini label="Valor em processamento" value={brl(totalPending)} />
        <Mini label="Total pago" value={brl(totalPaid)} gold />
      </div>

      <div className="rounded-2xl border border-border bg-white p-5">
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1 text-sm font-semibold text-brand-dark"><Filter size={15} /> Filtros</div>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="saques-filter-status" className="inp w-auto">
            <option value="">Todos os status</option>
            {Object.keys(WD_STATUS).map((k) => <option key={k} value={k}>{WD_STATUS[k][0]}</option>)}
          </select>
          <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="saques-filter-from" className="inp w-auto" />
          <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="saques-filter-to" className="inp w-auto" />
          <input type="number" placeholder="Valor min" value={f.min_amount} onChange={(e) => setF({ ...f, min_amount: e.target.value })} data-testid="saques-filter-min" className="inp w-28" />
          <button onClick={load} data-testid="saques-apply" className="rounded-xl px-4 py-2 text-sm font-semibold text-white" style={{ background: PURPLE }}><Search size={15} /></button>
        </div>

        {loading ? <div className="py-8 text-center text-sm text-[#9A8A98]">Carregando...</div>
          : rows.length === 0 ? <div className="py-8 text-center text-sm text-[#9A8A98]">Nenhum saque.</div>
          : (
            <div className="space-y-2" data-testid="saques-list">
              {rows.map((w) => {
                const [label, cls] = WD_STATUS[w.status] || [w.status, "bg-gray-100"];
                return (
                  <div key={w.id} className="rounded-xl border border-border p-3" data-testid={`saque-${w.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-brand-dark">{w.reseller_name} · {brl(w.amount)}</div>
                        <div className="text-[12px] text-[#8A7788]">{formatDate(w.created_at)} · PIX {w.pix?.masked_key} ({w.pix?.holder_name})</div>
                        <div className="text-[11px] text-[#9A8A98] flex items-center gap-1">
                          Saldo liquido {brl(w.reseller_net_balance)}
                          {w.reseller_responsibility_pending > 0 && <span className="flex items-center gap-0.5 text-red-600"><ShieldAlert size={11} /> resp. {brl(w.reseller_responsibility_pending)}</span>}
                        </div>
                        {w.reject_reason && <div className="text-[11px] text-red-600">Rejeitado: {w.reject_reason}</div>}
                        {w.status === "pago" && w.payment_reference && <div className="text-[11px] text-emerald-700">Comprovante: {w.payment_reference}</div>}
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {w.status === "solicitado" && <Btn onClick={() => act(w, "analyze")} disabled={busy} testid={`saque-analyze-${w.id}`}>Colocar em analise</Btn>}
                      {["solicitado", "em_analise"].includes(w.status) && <Btn onClick={() => act(w, "approve")} disabled={busy} primary testid={`saque-approve-${w.id}`}><CheckCircle2 size={13} /> Aprovar</Btn>}
                      {["solicitado", "em_analise", "aprovado"].includes(w.status) && <Btn onClick={() => setRejectModal(w)} disabled={busy} danger testid={`saque-reject-${w.id}`}><XCircle size={13} /> Rejeitar</Btn>}
                      {w.status === "aprovado" && <Btn onClick={() => setPayModal(w)} disabled={busy} primary testid={`saque-pay-${w.id}`}><DollarSign size={13} /> Marcar como pago</Btn>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {rejectModal && <ReasonModal title="Rejeitar saque" label="Motivo da rejeicao (obrigatorio)" onClose={() => setRejectModal(null)}
        onConfirm={(reason) => { act(rejectModal, "reject", { reason }); setRejectModal(null); }} testid="saque-reject-modal" />}
      {payModal && <PayModal wd={payModal} onClose={() => setPayModal(null)}
        onConfirm={(body) => { act(payModal, "pay", body); setPayModal(null); }} />}
    </div>
  );
}

function Btn({ children, onClick, disabled, primary, danger, testid }) {
  const cls = primary ? "text-white" : danger ? "border border-red-300 text-red-600" : "border border-border text-[#5B2CA0]";
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 ${cls}`} style={primary ? { background: PURPLE } : {}}>{children}</button>;
}

function ReasonModal({ title, label, onClose, onConfirm, testid }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <h2 className="font-display text-lg font-bold text-brand-dark">{title}</h2>
        <label className="mt-3 block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</span>
          <textarea rows={3} className="inp" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason-input" /></label>
        <div className="mt-5 flex gap-2">
          <button onClick={() => { if (!reason.trim()) { toast.error("Motivo obrigatorio"); return; } onConfirm(reason.trim()); }} data-testid="reason-confirm" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white" style={{ background: "#DC2626" }}>Confirmar</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function PayModal({ wd, onClose, onConfirm }) {
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="saque-pay-modal">
        <h2 className="font-display text-lg font-bold text-brand-dark">Marcar saque como pago</h2>
        <p className="mt-1 text-sm text-[#6B5A69]">{wd.reseller_name} · {brl(wd.amount)} · PIX {wd.pix?.masked_key}</p>
        <div className="mt-3 space-y-3">
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Referencia/comprovante (opcional)</span>
            <input className="inp" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="pay-reference" /></label>
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Observacao (opcional)</span>
            <input className="inp" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pay-note" /></label>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={() => onConfirm({ payment_reference: ref, note })} data-testid="pay-confirm" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white" style={{ background: PURPLE }}>Confirmar pagamento</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, gold }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid={`saques-mini-${label}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</div>
      <div className="font-display text-lg font-bold" style={{ color: gold ? "#C9960A" : PURPLE }}>{value}</div>
    </div>
  );
}
